<template>
	<WaitDialog :isActive="isWaiting" :title="statusMessage || waitTitle" />

	<v-container fluid class="pa-4">
		<!-- Network disruption warning -->
		<NetworkStatusBanner
			message="A network interruption was detected. Form generation may have stopped. Please refresh the page to continue."
		/>

		<!-- Error Messages Row -->
		<v-row v-if="errorMessages.length" class="mb-4">
			<v-col cols="12">
				<div class="error-notifications-container">
					<UserNotification v-for="(error, index) in errorMessages" :key="index" :message="error.content"
						:closable="true" />
				</div>
			</v-col>
		</v-row>

		<!-- Interactive Forms Stepper Row -->
		<v-row
			v-if="showInteractiveForms"
			class="mb-4">
			<v-col cols="12">
				<v-stepper mobile v-model="currentStep" editable>
					<template v-slot:default="{ }">
						<!-- Stepper Header -->
						<v-stepper-header class="pc">
							<!-- Steps for ghostDraftContent -->
							<template v-for="(content, index) in interactiveForms"
								:key="`step-ghostDraftContent-${index}`">
								<v-stepper-item :complete="currentStep > index + 1" :step="index + 1"
									:value="index + 1">
									<!-- Ghost Draft {{ index + 1 }} -->
								</v-stepper-item>
								<v-divider v-if="index < interactiveForms.length - 1"
									:key="`divider-ghostDraftContent-${index}`"></v-divider>
							</template>

							<!-- Final Step -->
							<v-stepper-item :complete="currentStep > totalSteps" :step="totalSteps" :value="totalSteps">
								Submit
							</v-stepper-item>
						</v-stepper-header>

						<!-- Stepper Window -->
						<v-stepper-window>
							<!-- Render ghostDraftContent -->
							<template v-for="(item, index) in interactiveForms"
								:key="`content-ghostDraftContent-${index}`">
								<v-stepper-window-item :value="index + 1">
									<!-- Render GhostDraftView for the current ghostDraftContent -->
									<div v-if="item !== null">
										<GhostDraftView ref="ghostDraftViewRef" :formDetails="item" />
									</div>
									<div v-else>
										<p>No template available for this GhostDraft content.</p>
									</div>
								</v-stepper-window-item>
							</template>

							<!-- Final Step -->
							<v-stepper-window-item :value="totalSteps">
								<v-row class="d-flex justify-center align-center h-100" style="min-height: 300px;">
									<v-col cols="3" class="text-center ">
										<h1 class="mb-4">Final Step</h1>
										<v-divider color="success"></v-divider>
										<p class="py-4">Please review all data before submitting.</p>
										<v-divider color="success"></v-divider>
										<Mbtn class="mt-5" color="primary" @click="handleSubmit"> Submit</Mbtn>
									</v-col>
								</v-row>
							</v-stepper-window-item>
						</v-stepper-window>

						<!-- Stepper Actions -->
						<v-stepper-actions v-if="currentStep < totalSteps">
							<!-- PREV -->
							<template #prev>
								<v-btn :disabled="!nextPrevEnabled" :loading="isWaiting"
									@click="() => handlePrevStep()">
									Previous Form
								</v-btn>
							</template>

							<!-- NEXT -->
							<template #next>
								<v-btn :disabled="!nextPrevEnabled" :loading="isWaiting"
									@click="() => handleNextStep()">
									Next Form
								</v-btn>
							</template>
						</v-stepper-actions>
					</template>

				</v-stepper>
			</v-col>
		</v-row>

		<!-- Generated Forms Row -->
		<v-row v-if="showPolicyForms" class="mb-4">
			<v-col cols="3">
				<h1 class="mb-4">Forms</h1>
				<v-list v-if="generatedFormDetails.length > 0">
					<v-list-item v-for="(form, index) in generatedFormDetails" :key="index" @click="scrollToForm(index)"
						:active="selectedFormIndex === index">
						<v-list-item-title>{{ form.formName }}</v-list-item-title>
					</v-list-item>
				</v-list>
				<p v-else class="text-caption">No forms available</p>
			</v-col>
			<v-col cols="9">
				<div v-if="generatedFormDetails.length > 0" class="h-100 overflow-y-auto">
					<v-tooltip text="Return to the interactive forms section to make edits" location="top">
						<template v-slot:activator="{ props }">
							<Mbtn v-if="policyFormsTracker?.hasInteractiveForms !== false && !policyFormsTracker?.isCompleted" class="mb-2" color="primary"
								v-bind="props" @click="editInteractiveForms">
								Edit Forms
							</Mbtn>
						</template>
					</v-tooltip> <v-infinite-scroll @load="loadMoreItems" :items="displayedForms"
						height="calc(100vh - 120px)" class="pdf-infinite-scroll">
						<template v-for="(form, index) in displayedForms" :key="index">
							<div :ref="el => setFormRef(el, index)" :data-form-index="index" class="pdf-section mb-6">
								<v-card class="mb-4 overflow-hidden" elevation="2">
									<v-card-title class="border-sm border-solid border-on-button-default bg-on-primary">
										{{ form.formName }}
									</v-card-title>
									<v-card-text class="pa-0">
										<iframe v-if="form.documentUrl" :src="getPdfUrl(form.documentUrl)"
											:ref="el => setPdfIframeRef(el, index)" width="100%"
											:height="getPdfHeight(index)" frameborder="0" class="pdf-iframe"
											@load="onPdfLoad(index)"></iframe>
										<p v-else class="text-caption pa-4">No document URL available for this form</p>
									</v-card-text>
								</v-card>
							</div>
						</template>
					</v-infinite-scroll>
				</div>
			</v-col>
		</v-row>
	</v-container>

	<!-- PDF iframe removed - Ghost Draft handles document display internally -->
</template>

<script lang="ts">

import { ref, onMounted, computed, ComponentPublicInstance, inject, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { GhostDraftForm, PolicyFormsTracker, ViewFormDetailsResponse, FormSystem, PolicyFormDetails } from '@/models/PrintDocumentModel';
import WaitDialog from '@/components/WaitDialog.vue';
import Mbtn from '@/components/BaseButton.vue';
import GhostDraftView from '@/components/GhostDraftView.vue';
import UserNotification from '@/components/UserNotification.vue';
import { usePrintDocumentsLogic } from '../composables/PrintDocuments/UsePrintDocumentsLogic';
import { EventBus, Events } from '@/contracts/EventBus';
import { MessageService } from '@/contracts/MessageService';
import { useCommercialApi } from '@/composables/UseCommercialApi';
import { CommercialApiConfig } from '@/services/CommercialApiConfig';
import NetworkStatusBanner from '@/components/NetworkStatusBanner.vue';

export default {
	name: 'PrintDocuments',
	route: {
		path: '/print-documents',
		meta: {
			requiresAuth: true,
			title: 'GhostDraft Documents'
		}
	},
	components: {
		WaitDialog,
		GhostDraftView,
		Mbtn,
		UserNotification,
		NetworkStatusBanner,
	},
	setup() {
		// ——— Route and Identifiers ———
		const route = useRoute();
		const eventBus = inject("eventBus") as EventBus;
		const { putInteractiveForms } = useCommercialApi(CommercialApiConfig);
		const waitTitle = ref<string>("Requesting forms...");
		const isComplete = ref<boolean>(false);

		const policyNumber = ref<string | null>(
			(route.query.policyNumber as string) || null
		);
		
		// Safely parse a string to an integer, returning null if invalid
		const tryParseInt = (value: string | undefined | null): number | null => {
			if (!value) return null;
			//we need to pass the radix value here so that "01" is parsed as 1, not 8
			const parsed = parseInt(value, 10);
			
			return isNaN(parsed) ? null : parsed;
		};

		// Amendment number comes in from the query string as "01", "02", etc.
		// If null or empty string, default to 0
		const amendmentNumber = ref<number>(
			tryParseInt(route.query.amendmentNumber as string) ?? 0
		);

		const coveragePartsAffected = ref('');
		const currentStep = ref<number>(1);
		const nextStep = ref<number>(1);

		const currentForm = ref<PolicyFormDetails | null>();

		// ——— GhostDraft Data ———
		const ghostDraftViewRef = ref<GhostDraftViewInstance[]>([]);

		type GhostDraftViewInstance = ComponentPublicInstance & {
			getGhostDraftFilledXml?: () => Promise<string | null>;
		};

		const ghostDraftForm = ref<GhostDraftForm | null>(null);
		const 
			{ 
				interactiveForms, 
				policyFormsTracker, 
				notifyInteractiveFormsComplete, 
				generatedFormDetails, 
				progressUpdate, 
				statusMessage, 
				errorMessages, 
				restoreInteractiveForms 
			} = usePrintDocumentsLogic(policyNumber, amendmentNumber);

		// ——— UI State ———
		const isWaiting = ref(true);
		const nextPrevEnabled = ref(false);
		const nextStepFunc = ref<(() => void) | null>(null);

		// ——— Policy Forms ———
		const selectedFormIndex = ref<number>(0);
		const displayedForms = ref<ViewFormDetailsResponse[]>([]);
		const formRefs = ref<(HTMLElement | null)[]>([]);
		const pdfIframeRefs = ref<(HTMLIFrameElement | null)[]>([]);
		const pdfHeights = ref<{ [key: number]: string }>({});
		const itemsPerLoad = ref<number>(5); // Load 5 items at a time for infinite scroll

		const totalSteps = computed(() => interactiveForms.value.length + 1); // +1 for the final step

		const showPolicyForms = computed(() =>
			generatedFormDetails.value != null
			&& policyFormsTracker.value != null
			&& (!policyFormsTracker.value.hasInteractiveForms || policyFormsTracker.value.interactiveFormsGenerated)
		);

		const showInteractiveForms = computed(() =>
			interactiveForms.value.length > 0
			&& policyFormsTracker.value != null
			&& policyFormsTracker.value.hasInteractiveForms
			&& !policyFormsTracker.value.interactiveFormsGenerated
		);

		function handlePrevStep() {
			if (isWaiting.value) return

			nextStep.value = currentStep.value - 1;

			if (nextStep.value < 1) {
				nextStep.value = 1;
			}

			handleStepChanged();
		}

		async function handleNextStep() {
			if (isWaiting.value) return;

			nextStep.value = currentStep.value + 1;

			if (nextStep.value > totalSteps.value + 1) {
				nextStep.value = totalSteps.value + 1;
			}

			handleStepChanged();
		}

		const handleSubmit = async () => {
			// this protects against still trying to save the last step...
			if (isWaiting.value) return;

			waitTitle.value = "Generating forms...";
			isWaiting.value = true;

			await notifyInteractiveFormsComplete();

			console.log('All forms submitted and processed.');
		}

		const handleStepChanged = () => {
			const ghostDraftIndex = currentStep.value - 1;
			const ghostDraftView = ghostDraftViewRef.value[ghostDraftIndex];

			//signal that we're about to do some work
			waitTitle.value = "Saving...";
			isWaiting.value = true;

			// tell the current component that we'd like to go to another steps
			// all components are going to receive this message, so they need to know which one is being requested
			currentForm.value = interactiveForms.value[ghostDraftIndex];
			eventBus.emit(Events.INTERACTIVE_FORMS_UPDATE_REQUESTED, currentForm.value);
		}

		const selectForm = (index: number) => {
			selectedFormIndex.value = index;
		}

		// ——— Infinite Scroll Functions ———
		const setFormRef = (el: Element | ComponentPublicInstance | null, index: number) => {
			if (el && 'scrollIntoView' in el) {
				formRefs.value[index] = el as HTMLElement;
			}
		};

		const loadMoreItems = ({ done }: { done: (status: 'ok' | 'empty' | 'error') => void }) => {
			const currentLength = displayedForms.value.length;
			const remainingItems = generatedFormDetails.value.length - currentLength;

			if (remainingItems > 0) {
				// Load next batch of items
				const nextBatch = generatedFormDetails.value.slice(
					currentLength,
					currentLength + itemsPerLoad.value
				);
				displayedForms.value.push(...nextBatch);

				// Check if we've loaded all items
				if (displayedForms.value.length >= generatedFormDetails.value.length) {
					done('empty'); // No more items to load
				} else {
					done('ok'); // More items available
				}
			} else {
				done('empty'); // No more items to load
			}
		};

		const loadFormsUpToIndex = async (targetIndex: number): Promise<void> => {
			return new Promise((resolve) => {
				const loadBatch = () => {
					const currentLength = displayedForms.value.length;

					// If we've already loaded enough forms, resolve
					if (targetIndex < currentLength) {
						resolve();
						return;
					}

					// Calculate how many more forms we need to load
					const remainingItems = generatedFormDetails.value.length - currentLength;

					if (remainingItems > 0) {
						// Load enough forms to reach the target index, or all remaining forms
						const formsNeeded = (targetIndex + 1) - currentLength;
						const formsToLoad = Math.min(formsNeeded, remainingItems);

						const nextBatch = generatedFormDetails.value.slice(
							currentLength,
							currentLength + formsToLoad
						);

						displayedForms.value.push(...nextBatch);

						// If we still need more forms and there are more available, continue loading
						if (targetIndex >= displayedForms.value.length && displayedForms.value.length < generatedFormDetails.value.length) {
							// Use setTimeout to allow DOM to update between batches
							setTimeout(loadBatch, 50);
						} else {
							resolve();
						}
					} else {
						// No more forms available
						resolve();
					}
				};

				loadBatch();
			});
		};

		const scrollToForm = (index: number) => {
			selectedFormIndex.value = index;

			// Check if the form at the given index is already loaded
			if (index < displayedForms.value.length) {
				// Form is already loaded, scroll to it immediately
				const formElement = formRefs.value[index];
				if (formElement) {
					formElement.scrollIntoView({
						behavior: 'smooth',
						block: 'start'
					});
				}
			} else {
				// Form is not loaded yet, load forms up to this index
				loadFormsUpToIndex(index).then(() => {
					// After loading, wait a tick for DOM to update, then scroll
					setTimeout(() => {
						const formElement = formRefs.value[index];
						if (formElement) {
							formElement.scrollIntoView({
								behavior: 'smooth',
								block: 'start'
							});
						}
					}, 100); // Small delay to ensure DOM has updated
				});
			}
		};

		// ——— PDF Height Management ———
		const setPdfIframeRef = (el: Element | ComponentPublicInstance | null, index: number) => {
			if (el && el instanceof HTMLIFrameElement) {
				pdfIframeRefs.value[index] = el;
			}
		};

		const getPdfUrl = (originalUrl: string) => {
			// Add URL parameters to optimize PDF display
			// These parameters can help control how the PDF is displayed
			const url = new URL(originalUrl);

			// Add parameters for better PDF viewing experience
			// Note: These work with many PDF viewers but may not work with all hosts
			url.searchParams.set('view', 'FitH'); // Fit to horizontal width
			url.searchParams.set('toolbar', '0'); // Hide toolbar
			url.searchParams.set('navpanes', '0'); // Hide navigation panes
			url.searchParams.set('scrollbar', '0'); // Hide scrollbar

			return url.toString();
		};

		const getPdfHeight = (index: number) => {
			return pdfHeights.value[index] || '1400px'; // Increased default height for better PDF viewing
		};

		const onPdfLoad = (index: number) => {
			try {
				const iframe = pdfIframeRefs.value[index];
				if (!iframe) return;

				// For external PDFs, we need to use a heuristic approach
				// Most PDF forms are either single page or multi-page documents
				// We'll set a generous height that should work for most cases

				// Check if we can determine any information about the PDF
				// If the PDF has specific parameters in the URL, we might be able to adjust
				const documentUrl = displayedForms.value[index]?.documentUrl || '';

				let estimatedHeight = '1400px'; // Default generous height

				// If URL contains indicators of page count or size, we could adjust
				// For now, use a height that should accommodate most PDF forms without scrollbars
				if (documentUrl.includes('sample')) {
					// Sample documents are typically shorter
					estimatedHeight = '1200px';
				}

				// Dynamic approach: Try to detect PDF characteristics from the iframe
				// After the PDF loads, we can attempt to analyze its content height
				setTimeout(() => {
					try {
						// Approach 1: Check if the iframe has scrollbars after initial load
						const hasVerticalScrollbar = iframe.contentWindow &&
							iframe.contentWindow.document &&
							iframe.contentWindow.document.body.scrollHeight > iframe.clientHeight;

						if (hasVerticalScrollbar) {
							// If there are scrollbars, the PDF is likely longer than our current height
							estimatedHeight = '2400px'; // Increase height for multi-page content
							pdfHeights.value[index] = estimatedHeight;
						}
					} catch (error) {
						// CORS restrictions prevent access to iframe content
						// Fallback: Use URL-based heuristics for common patterns

						// Look for common indicators in URLs that suggest multi-page documents
						const multiPageIndicators = [
							'form', 'application', 'instruction', 'guide', 'manual', 'report',
							'tax', 'legal', 'contract', 'agreement', 'policy', 'terms'
						];

						const urlLower = documentUrl.toLowerCase();
						const likelyMultiPage = multiPageIndicators.some(indicator =>
							urlLower.includes(indicator)
						);

						// Check file size hints in URL or filename
						const hasComplexFilename = documentUrl.split('/').pop()?.split('-').length > 3;

						if (likelyMultiPage || hasComplexFilename) {
							estimatedHeight = '2200px'; // Increased height for likely multi-page PDFs
							pdfHeights.value[index] = estimatedHeight;
						}
					}
				}, 1000); // Give the PDF time to fully load

				pdfHeights.value[index] = estimatedHeight;

			} catch (error) {
				console.warn('Could not determine PDF height, using default height');
				pdfHeights.value[index] = '1400px';
			}
		};

		// ——— External Logic & Composables ———

		const formDetailsUpdated = async (updatedForm: PolicyFormDetails) => {
			if (currentForm.value && updatedForm.formName === currentForm.value.formName) {
				currentForm.value = updatedForm;

				try {
					// Merge updated form into policyFormsTracker.value.policyForms
					for (let i = 0; i < policyFormsTracker.value.policyForms.length; i++) {
						if (policyFormsTracker.value.policyForms[i].formName === updatedForm.formName) {
							policyFormsTracker.value.policyForms[i] = updatedForm;
							break; // Exit loop once we find and update the matching form
						}
					}

					// Call putInteractiveForms from useCommercialApi
					const result = await putInteractiveForms(policyFormsTracker.value);

					currentStep.value = nextStep.value;

					console.log('Interactive forms updated successfully:', result);
					console.log('Form details updated:', updatedForm);
				} catch (error) {
					console.error('Error updating interactive forms:', error);
				} finally {
					isWaiting.value = false;
				}
			}
		};

		const stepButtonsUpdated = (enabled: boolean) => {
			nextPrevEnabled.value = enabled;

			if (enabled) {
				// Only stop waiting if buttons are enabled
				isWaiting.value = false;
			}
		};

		const formsGenerated = () => {
			isWaiting.value = false;
			currentStep.value = 1;
		};

		const editInteractiveForms = async () => {

			// Set waiting state to show loading dialog
			waitTitle.value = "Restoring interactive forms...";
			isWaiting.value = true;

			// Restore interactive forms from tracker data
			await restoreInteractiveForms();

			// Reset to first step
			currentStep.value = 1;
			nextStep.value = 1;
		};

		onMounted(async () => {
			// Remove legacy window component instance setup since we no longer need PDF content
			isWaiting.value = true;

			// Initialize test data to see the iframe in action
			// initializeTestData();

			// Comment out existing logic for testing

			// Simpulate signalR hit for initial load
			//await initTestHook();

			try {
				//This area will contain the logic to handle a signalR message when recieved
				// ------
				// This area will contain the logic to check if the policy has been hit before
				// by going to the API and verifying the policyNumber
				//let isPolicyNotInProgress = await printDocuments.verifyPolicyNotInProgress();

				eventBus.on(Events.INTERACTIVE_FORMS_UPDATE_COMPLETE, formDetailsUpdated);
				eventBus.on(Events.INTERACTIVE_FORMS_DATACAPTURE_READY, stepButtonsUpdated);
				eventBus.on(Events.INTERACTIVE_FORMS_GENERATED, formsGenerated);

				//call API to fetch data for 
			} catch (error) {
				console.error('Error loading page:', error);
			}

		});

		onUnmounted(() => {
			// Clean up event listeners
			eventBus.off(Events.INTERACTIVE_FORMS_UPDATE_COMPLETE, formDetailsUpdated);
			eventBus.off(Events.INTERACTIVE_FORMS_DATACAPTURE_READY, stepButtonsUpdated);
			eventBus.off(Events.INTERACTIVE_FORMS_GENERATED, formsGenerated);

		});

		const initTestHook = async () => {
			// Testing material for UI form collection
			isWaiting.value = true;

			try {
				const testGhostDraftDocument = async () => {
					const testGDocument = '/GhostDraft-test.JSON';
					const response = await fetch(testGDocument);
					const content = await response.json();
					return content;
				};
				const ghostDraftData = await testGhostDraftDocument();
				console.log('Ghost Draft Test Data:', ghostDraftData);
				// ghostDraft.parseGhostDraftData(ghostDraftData);

			} catch (error) {
				console.error('Error initializing Ghost Draft test data:', error);
				// Handle the error gracefully - perhaps show a notification to the user
			}
		};

		return {
			// Your reactive properties and methods here
			coveragePartsAffected,
			isWaiting,
			currentStep,
			isComplete,
			interactiveForms,
			handleNextStep,
			handlePrevStep,
			handleSubmit,
			totalSteps,
			ghostDraftViewRef,
			// New SignalR data structures
			policyFormsTracker,
			ghostDraftForm,
			waitTitle,
			// Policy Forms
			selectedFormIndex,
			selectForm,
			// initializeTestData,
			// Infinite Scroll
			displayedForms,
			formRefs,
			itemsPerLoad,
			setFormRef,
			loadMoreItems,
			loadFormsUpToIndex,
			nextPrevEnabled,
			scrollToForm,
			// PDF Height Management
			pdfIframeRefs,
			pdfHeights,
			setPdfIframeRef,
			getPdfUrl,
			getPdfHeight,
			onPdfLoad,
			generatedFormDetails,
			statusMessage,
			showPolicyForms,
			showInteractiveForms,
			progressUpdate,
			errorMessages,
			editInteractiveForms,
			restoreInteractiveForms
		};
	},
};
</script>

<style scoped>
/* Your styles here */

.bg-sheet {
	color: rgb(var(--v-theme-primary));
}

.pdf-container {
	height: 100vh;
	overflow-y: auto;
}

.pdf-infinite-scroll {
	height: 100%;
}

.pdf-section {
	scroll-margin-top: 20px;
}

.pdf-iframe {
	border: 1px solid #ddd;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(var(--v-theme-surface));
	display: block;
	overflow: hidden;
	/* Hide iframe scrollbars */
	width: 100%;
}

/* .form-list-item {
	cursor: pointer;
	transition: background-color 0.2s ease;
}

.form-list-item:hover {
	background-color: rgb(var(--v-theme-on-button-default));
} */
</style>